import { useTranslation } from "react-i18next";

import { ISalvageSummary, ISalvageUserProfit, SalvageStage } from "../../../data/useSalvage";
import { useUserLookup } from "../../../hooks/useUserLookup";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";

type Tile = {
  stage?: SalvageStage;
  value: string;
  label: string;
  bgClass: string;
  textClass: string;
};

function daysLabel(value?: number | null) {
  if (value == null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-gb", { maximumFractionDigits: 1 }).format(value);
}

function userName(
  findUser: (userId: string) => { fullname?: string } | undefined,
  userId?: string | null
) {
  if (!userId) return "—";
  return findUser(userId)?.fullname?.trim() || "—";
}

export default function SalvageDashboard({
  summary,
  loading,
  canViewPrices,
  stage,
  onStageChange,
}: {
  summary?: ISalvageSummary;
  loading: boolean;
  canViewPrices: boolean;
  stage?: SalvageStage;
  onStageChange: (stage?: SalvageStage) => void;
}) {
  const { t } = useTranslation("leads");
  const { findUser } = useUserLookup();
  const users = summary?.profitByUser ?? [];

  const money = (value?: number | null) =>
    canViewPrices && value != null && !Number.isNaN(value) ? PriceFormat(value) : "—";

  const tiles: Tile[] = [
    {
      stage: "awaitingCollection",
      value: String(summary?.awaitingCollection ?? 0),
      label: t("Awaiting Copart collection"),
      bgClass: "bg-amber-50",
      textClass: "text-amber-700",
    },
    {
      stage: "awaitingAuction",
      value: String(summary?.awaitingAuction ?? 0),
      label: t("Collected, awaiting auction"),
      bgClass: "bg-sky-50",
      textClass: "text-sky-700",
    },
    {
      stage: "sold",
      value: String(summary?.sold ?? 0),
      label: t("Sold at auction"),
      bgClass: "bg-emerald-50",
      textClass: "text-emerald-700",
    },
    {
      stage: "returned",
      value: String(summary?.returned ?? 0),
      label: t("Not sold, returned to GP"),
      bgClass: "bg-rose-50",
      textClass: "text-rose-700",
    },
    {
      value: daysLabel(summary?.averageDaysInStock),
      label: t("Avg days in stock"),
      bgClass: "bg-violet-50",
      textClass: "text-violet-700",
    },
    {
      value: money(summary?.averageProfit),
      label: t("Average profit"),
      bgClass: "bg-pink-50",
      textClass: "text-pink-700",
    },
  ];

  const moneyTiles: Tile[] = [
    {
      value: money(summary?.projectedRevenue),
      label: t("Projected total revenue"),
      bgClass: "bg-indigo-50",
      textClass: "text-indigo-700",
    },
    {
      stage: "notSold",
      value: money(summary?.stockValueAwaitingSale),
      label: t("Stock awaiting sale"),
      bgClass: "bg-slate-50",
      textClass: "text-slate-700",
    },
    {
      stage: "awaitingAuction",
      value: money(summary?.awaitingAuctionCost),
      label: t("Cost awaiting auction"),
      bgClass: "bg-sky-50",
      textClass: "text-sky-700",
    },
    {
      stage: "notSold",
      value: money(summary?.notYetSoldCost),
      label: t("Purchase cost not yet sold"),
      bgClass: "bg-amber-50",
      textClass: "text-amber-700",
    },
    {
      stage: "sold",
      value: money(summary?.soldRevenue),
      label: t("Sold revenue"),
      bgClass: "bg-emerald-50",
      textClass: "text-emerald-700",
    },
    {
      stage: "sold",
      value: money(summary?.soldProfit),
      label: t("Sold profit"),
      bgClass: "bg-pink-50",
      textClass: "text-pink-700",
    },
    {
      stage: "returned",
      value: money(summary?.returnedRedeliveryCost),
      label: t("Returned redelivery cost"),
      bgClass: "bg-rose-50",
      textClass: "text-rose-700",
    },
  ];

  const toggleStage = (next?: SalvageStage) => {
    onStageChange(next && next !== stage ? next : undefined);
  };

  const renderTiles = (items: Tile[]) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {items.map((tile) => {
        const selected = !!tile.stage && tile.stage === stage;
        return (
          <button
            key={tile.label}
            type="button"
            disabled={!tile.stage}
            onClick={() => toggleStage(tile.stage)}
            className={classNames(
              "rounded-lg border p-3 text-left shadow-sm",
              tile.bgClass,
              tile.stage ? "cursor-pointer hover:brightness-95" : "cursor-default",
              selected ? "border-current ring-2 ring-current" : "border-transparent",
              tile.textClass
            )}
          >
            <p className="text-2xl font-semibold tabular-nums">{loading && !summary ? "—" : tile.value}</p>
            <p className="mt-1 text-sm">{tile.label}</p>
          </button>
        );
      })}
    </div>
  );

  return (
    <section aria-label={t("Salvage dashboard")} className="space-y-4">
      {renderTiles(tiles)}
      {canViewPrices && renderTiles(moneyTiles)}

      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">{t("Profit by user")}</h2>
        <p className="mt-1 text-sm text-gray-500">
          {t("Who booked the collection, once a vehicle has a final auction price.")}
        </p>
        {loading && users.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">{t("Loading summary")}</p>
        ) : users.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">{t("No sold vehicles yet.")}</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4">{t("User")}</th>
                  <th className="py-2 pr-4 text-right">{t("Sold")}</th>
                  {canViewPrices && <th className="py-2 pr-4 text-right">{t("Total profit")}</th>}
                  {canViewPrices && <th className="py-2 text-right">{t("Average profit")}</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((row) => (
                  <ProfitRow
                    key={row.bookedCollectionBy || "unassigned"}
                    row={row}
                    name={userName(findUser, row.bookedCollectionBy)}
                    canViewPrices={canViewPrices}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function ProfitRow({
  row,
  name,
  canViewPrices,
}: {
  row: ISalvageUserProfit;
  name: string;
  canViewPrices: boolean;
}) {
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <td className="py-2 pr-4 text-gray-700">{name}</td>
      <td className="py-2 pr-4 text-right font-medium tabular-nums text-gray-900">{row.sold}</td>
      {canViewPrices && (
        <td className="py-2 pr-4 text-right font-medium tabular-nums text-gray-900">{PriceFormat(row.totalProfit)}</td>
      )}
      {canViewPrices && (
        <td className="py-2 text-right font-medium tabular-nums text-gray-900">{PriceFormat(row.averageProfit)}</td>
      )}
    </tr>
  );
}
